<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import type { IAvailabilityChoice } from '@/domain/catalog/availability';
  import type { ICatalogAvailabilityPanel } from '@/composition/catalog/catalog-page-state';

  /**
   * «¿Dónde oculta "{nombre}"?» / «¿Dónde lo muestra?» (plan §5.9): the
   * scope of a switch change in multi-branch stores, as full-width buttons
   * that name the consequence. The chain-wide one is the strong button:
   * dark in the popover, orange in the phone sheet.
   */
  const props = defineProps<{
    panel: ICatalogAvailabilityPanel;
    /** The product's or category's name. */
    name: string;
    branchName: string;
    /** The store lists hidden products as «No disponible». */
    showDisabledItems: boolean;
    /** Rendered in the phone sheet. */
    sheet: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'choose', choice: IAvailabilityChoice): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const isCategory = computed(() => props.panel.entity === 'category');

  const title = computed(() =>
    props.panel.turnOn
      ? t(
          isCategory.value
            ? 'menuPage.availability.showCategoryTitle'
            : 'menuPage.availability.showTitle'
        )
      : t('menuPage.availability.hideTitle', { name: props.name })
  );

  const body = computed(() => {
    if (props.panel.turnOn)
      return t(
        isCategory.value
          ? 'menuPage.availability.showCategoryBody'
          : 'menuPage.availability.showBody'
      );
    if (isCategory.value) return t('menuPage.availability.hideCategoryBody');
    return t(
      props.showDisabledItems
        ? 'menuPage.availability.hideBodyUnavailable'
        : 'menuPage.availability.hideBody'
    );
  });

  function choiceText(choice: IAvailabilityChoice): string {
    const action = props.panel.turnOn ? 'show' : 'hide';
    return choice.scope === 'here'
      ? t(`menuPage.availability.${action}Here`, { branch: props.branchName })
      : t(`menuPage.availability.${action}Everywhere`);
  }
</script>

<template>
  <div class="v2-catalog-availability-chooser">
    <h2 class="chooser-title">{{ title }}</h2>
    <p class="chooser-body">{{ body }}</p>
    <div class="chooser-choices">
      <PosUiButton
        v-for="choice in panel.choices"
        :key="choice.scope"
        class="chooser-choice"
        :color="choice.scope === 'everywhere' && sheet ? 'primary' : 'neutral'"
        :variant="choice.scope === 'everywhere' ? 'solid' : 'outline'"
        block
        @click="emit('choose', choice)"
        >{{ choiceText(choice) }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-availability-chooser {
    .chooser-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .chooser-body {
      @apply tw-mb-0 tw-mt-1.5 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
    }

    .chooser-choices {
      @apply tw-mt-4;

      .chooser-choice + .chooser-choice {
        @apply tw-mt-2;
      }
    }
  }
</style>
